import { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  CreatePromoTagPayload,
  GetPromoTagsParams,
  PromoTag,
  UpdatePromoTagPayload,
  buildTagId,
  createPromoTag,
  getPromoTags,
  updatePromoTag,
} from '~/services/PromoService';

type UsePromoTagResult = {
  promoTags: Array<PromoTag>;
  isLoading: boolean;
  isLoadingUpdate: boolean;
  isLoadingCreate: boolean;
  error: string | null;
  fetchPromoTags: (query: GetPromoTagsParams) => Promise<void>;
  createTag: (payload: CreatePromoTagPayload) => Promise<PromoTag | null>;
  updateTag: (payload: UpdatePromoTagPayload) => Promise<PromoTag | null>;
};

const usePromoTagManagement = (): UsePromoTagResult => {
  const [promoTags, setPromoTags] = useState<Array<PromoTag>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingCreate, setIsLoadingCreate] = useState<boolean>(false);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const fetchPromoTags = useCallback(
    async (query: GetPromoTagsParams): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getPromoTags(query);
        setPromoTags(response.result.promoTags);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch promo tags';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  const createTag = useCallback(
    async (payload: CreatePromoTagPayload): Promise<PromoTag | null> => {
      setIsLoadingCreate(true);
      setError(null);

      try {
        const response = await createPromoTag(payload);
        const newTag = response.result;

        setPromoTags((prevTags) => [...prevTags, newTag]);

        enqueueSnackbar('Promo tag created successfully', { variant: 'success' });
        return newTag;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create promo tag';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
        return null;
      } finally {
        setIsLoadingCreate(false);
      }
    },
    [enqueueSnackbar],
  );

  const updateTag = useCallback(
    async (payload: UpdatePromoTagPayload): Promise<PromoTag | null> => {
      setIsLoadingUpdate(true);
      setError(null);

      try {
        const response = await updatePromoTag(payload);
        const updatedTag = response.result;

        setPromoTags((prevTags) => prevTags.map((tag) => (buildTagId(tag) === buildTagId(payload) ? updatedTag : tag)));

        enqueueSnackbar('Promo tag updated successfully', { variant: 'success' });
        return updatedTag;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update promo tag';
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
        return null;
      } finally {
        setIsLoadingUpdate(false);
      }
    },
    [enqueueSnackbar],
  );

  return {
    promoTags,
    isLoading,
    isLoadingUpdate,
    isLoadingCreate,
    error,
    fetchPromoTags,
    createTag,
    updateTag,
  };
};

export default usePromoTagManagement;
